import { z } from 'zod';
import { assertNever } from '../lib/assert-never.js';
import {
  EMPTY_RICH_TEXT,
  EXPERIENCE_TEXT_MAX,
  type RichText,
  richTextPlain,
  richTextSchema,
} from './experience.js';
import type { NoyauBlockMode, NoyauCode } from './noyau.js';
import {
  saveDraftRequestSchema,
  saveVersionRequestSchema,
  usageLineSchema,
  versionedContentSchema,
  versionedDetailSchema,
} from './versioning.js';

/**
 * « Les textes des noyaux » (noyau.md §6): ONE versioned object, a singleton,
 * on the common layer of §4 of the refonte - history, the two pointers, the
 * autosaved draft, « Enregistrer une nouvelle version », « Passer en
 * production », rollback by moving a pointer.
 *
 * What is here is the CONTENT and the shapes the layer carries it in, built by
 * the builders of `versioning.ts` like the four objects before it. The store,
 * the routes and the tab of Réglages are the lot of issue #148.
 *
 * ## A singleton, and what that changes (N8)
 *
 * No list, no explicit creation, no retirement, no duplication, no renaming:
 * the FIRST save from Réglages writes the entity and its v1 in one transaction,
 * under the one identifier below, and every later save is a version of it. So
 * there is no rename schema here, and the name a content carries is the fixed
 * one - the layer copies `name` onto the entity and into each version (§4.1),
 * and it has to be something.
 *
 * ## Why the id is a CONSTANT
 *
 * The reference of an experience is `noyauTexts: { tag }` and carries no id
 * (N13) - there is only one object to name. But the usage walk of §4 is keyed
 * on `(kind, id, tag)`, and the cascade of a session start resolves `{ id, tag }`
 * strictly: both need an id without reading the base, from a content alone. A
 * constant is the id every reader can write down, and the store writes the
 * entity under it.
 *
 * ## What a version carries (§6.2)
 *
 * Per noyau of the table of §2.1, THREE texts: the first text (shown at the
 * first choice), the second text (shown at the second choice) and the
 * portrait (shown by the portrait mode). Eighteen texts, each in the RESTRICTED
 * FORMAT of the Texte block (`richTextSchema`), rendered by the same pure
 * function and bounded by `EXPERIENCE_TEXT_MAX`. The NAME and the TRIPLET of a
 * noyau are not in the version: they are in the table.
 *
 * Grouped BY NOYAU, the way Réglages > Noyaux edits them (§6.3: six cards of
 * three fields) and the way a session reads them (the first text of the
 * principal, the portrait of the retained). Six explicit keys rather than a
 * record over the enum: a Zod record over an enum is PARTIAL, and every reader
 * would branch on a noyau that cannot be missing.
 *
 * « Enregistrer une nouvelle version » demands the eighteen texts NON-EMPTY
 * (N7): a version is always playable, and the autosaved draft absorbs a partial
 * entry while the specialist delivers the rest. So the version's schema refuses
 * a blank text and the draft's does not.
 */

/** The identifier of the singleton entity (N8, N13) - see the head of this file. */
export const NOYAU_TEXTS_ID = 'noyau-texts';

/** Its name, fixed: what the layer copies onto the entity and into each version. */
export const NOYAU_TEXTS_NAME = 'Les textes des noyaux';

/** Whether one text counts as written (N7): something beyond whitespace, bounds kept. */
export function noyauTextFilled(text: RichText): boolean {
  return richTextPlain(text).trim() !== '';
}

/**
 * One text of a VERSION: written, and within the 4000 characters of H23 -
 * measured on the words, like the Texte block's.
 */
const writtenTextSchema = richTextSchema.superRefine((text, ctx) => {
  if (!noyauTextFilled(text)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'A version of the texts of the noyaux carries the eighteen texts, none of them empty.',
    });
  }
  if (richTextPlain(text).length > EXPERIENCE_TEXT_MAX) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `A text of a noyau holds at most ${EXPERIENCE_TEXT_MAX} characters.`,
    });
  }
});

/**
 * One text of a DRAFT: anything the editor holds, blank included, still within
 * the bound - a draft is refused nothing but what no version could ever carry.
 */
const draftTextSchema = richTextSchema.default(EMPTY_RICH_TEXT).superRefine((text, ctx) => {
  if (richTextPlain(text).length <= EXPERIENCE_TEXT_MAX) return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: `A text of a noyau holds at most ${EXPERIENCE_TEXT_MAX} characters.`,
  });
});

/** The three texts of ONE noyau, in a version (§6.2). */
export const noyauTextEntrySchema = z.object({
  /** Shown at the FIRST choice, facing the other candidate's. */
  firstText: writtenTextSchema,
  /** Shown at the SECOND choice, facing the opposite's. */
  secondText: writtenTextSchema,
  /** Shown by the portrait mode: its qualities, its motto, what the team wants to say. */
  portrait: writtenTextSchema,
});
export type NoyauTextEntry = z.infer<typeof noyauTextEntrySchema>;

/** The three texts of ONE noyau, in a draft: each defaulted to the empty text. */
export const noyauTextEntryDraftSchema = z.object({
  firstText: draftTextSchema,
  secondText: draftTextSchema,
  portrait: draftTextSchema,
});
export type NoyauTextEntryDraft = z.infer<typeof noyauTextEntryDraftSchema>;

/** The EMPTY entry, what a noyau of a draft nobody has touched holds. */
export const EMPTY_NOYAU_TEXT_ENTRY: NoyauTextEntryDraft = {
  firstText: EMPTY_RICH_TEXT,
  secondText: EMPTY_RICH_TEXT,
  portrait: EMPTY_RICH_TEXT,
};

/**
 * The six noyaux as explicit keys, in the order of the table - checked against
 * `NOYAU_CODES` of `noyau.ts` by a test, so a seventh code cannot be added to
 * the table without its texts.
 */
function byNoyau<Entry extends z.ZodTypeAny>(entry: Entry) {
  return z.object({
    SGP: entry,
    SPG: entry,
    GSP: entry,
    GPS: entry,
    PGS: entry,
    PSG: entry,
  });
}

/**
 * The fixed name, as a schema: that one string, or nothing and then that one.
 *
 * A LITERAL and not a bounded string (review of !462): the layer copies the
 * name a version carries onto the entity (§4.1, `saveVersion` in
 * `versioning/store.ts`), so a string schema here was a rename by the back
 * door - one `POST .../versions` with another name, and the singleton that has
 * « ni renommage » (N8) was called something else. Nothing was stored under
 * the looser shape when it narrowed, so no row stops parsing.
 */
const noyauTextsNameSchema = z.literal(NOYAU_TEXTS_NAME).default(NOYAU_TEXTS_NAME);

/**
 * The CONTENT of one version - the eighteen texts, and the fixed name the layer
 * wants (§4.1).
 *
 * Not `.strict()`, like every stored shape: a field added later must not make
 * today's versions unreadable.
 */
export const noyauTextsContentSchema = z.object({
  name: noyauTextsNameSchema,
  noyaux: byNoyau(noyauTextEntrySchema),
});
export type NoyauTextsContent = z.infer<typeof noyauTextsContentSchema>;

/**
 * The text a Noyau block SHOWS for one noyau, by its mode (§3.2, §6.2): the
 * first text on the first choice, the second text on the second choice, the
 * portrait on the portrait.
 *
 * The one reading of « which of the three », for the session that serves the
 * cards and the portrait from the version it froze, and for the two previews
 * that show the real texts of a pair of example (N20). The texts carry no
 * variable (§6.2), so what this answers is rendered as it stands - a `{{...}}`
 * a specialist typed is read as the words it is, never resolved.
 */
export function noyauTextFor(
  texts: NoyauTextsContent,
  noyau: NoyauCode,
  mode: NoyauBlockMode,
): RichText {
  const entry = texts.noyaux[noyau];
  switch (mode) {
    case 'first-choice':
      return entry.firstText;
    case 'second-choice':
      return entry.secondText;
    case 'portrait':
      return entry.portrait;
    default:
      return assertNever(mode, 'noyau text mode');
  }
}

/**
 * The content of a DRAFT: every noyau defaulted to three empty texts, so an
 * autosave of one field is a legal draft - « le brouillon autosauvé absorbe une
 * saisie partielle » (N7). The name is the same literal: a draft cannot carry a
 * name no version could.
 */
export const noyauTextsDraftContentSchema = z.object({
  name: noyauTextsNameSchema,
  noyaux: byNoyau(noyauTextEntryDraftSchema.default(EMPTY_NOYAU_TEXT_ENTRY)).default({}),
});
export type NoyauTextsDraftContent = z.infer<typeof noyauTextsDraftContentSchema>;

/** Everything the editor of Réglages > Noyaux reads in one call (§4, §6.3). */
export const noyauTextsDetailSchema = versionedDetailSchema(
  noyauTextsContentSchema,
  noyauTextsDraftContentSchema,
);
export type NoyauTextsDetail = z.infer<typeof noyauTextsDetailSchema>;

/** One version read in full, for « Voir » and « Repartir de cette version ». */
export const noyauTextsVersionSchema = versionedContentSchema(noyauTextsContentSchema);
export type NoyauTextsVersion = z.infer<typeof noyauTextsVersionSchema>;

/** « Enregistrer une nouvelle version » - and, the first time, the birth of the singleton (N8). */
export const noyauTextsSaveVersionSchema = saveVersionRequestSchema(noyauTextsContentSchema);
export type NoyauTextsSaveVersion = z.infer<typeof noyauTextsSaveVersionSchema>;

/** An autosave of the draft, on the partial content. */
export const noyauTextsSaveDraftSchema = saveDraftRequestSchema(noyauTextsDraftContentSchema);
export type NoyauTextsSaveDraft = z.infer<typeof noyauTextsSaveDraftSchema>;

/**
 * Who reads the texts, per pointer OF THE TEXTS (§6.5; review of !462) - what
 * the save box and « Passer en production » show before the click.
 *
 * `production` lists the experiences whose reference reads the texts'
 * `production` pointer, `development` those whose reference reads their
 * `développement` one: « avec le tag de leur référence » (§6.5). The tags of a
 * line are still where that experience's OWN pointers sit, as on every usage
 * line - but only the pointers whose version reads the texts under that tag.
 * An experience whose production version forces `noyauTexts: { tag:
 * 'development' }` is therefore absent from `production`, and an experience in
 * development left on the default `production` tag is in it, under its
 * `development` pill: moving the texts' production pointer changes what its
 * development sessions read.
 *
 * Served by `GET /api/noyau-texts/{id}/usage`, which answers BEFORE the first
 * version as well (N8): the box of the first save shows the experiences that
 * will start with it, where the detail has nothing to say - it is a 404 until
 * then. « Utilisé dans » keeps the layer's rule and reads the detail.
 */
export const noyauTextsUsageSchema = z
  .object({
    production: z.array(usageLineSchema),
    development: z.array(usageLineSchema),
  })
  .strict();
export type NoyauTextsUsage = z.infer<typeof noyauTextsUsageSchema>;
